import React, { useEffect } from 'react';
import { Div, Typography } from '@icecreamsocial/components';
import { useHistory } from 'react-router-dom';
import { Offer, CodeUnlocker, PageWrapper } from '../../../../presentational';
import { flags } from '../../../../../utils/featureFlags';
import { FormattedMessage, useIntl } from 'react-intl';
import PopsicleLoader from '../../../../presentational/PopsicleLoader';
import { usePostHog } from 'posthog-js/react';
import { getPersistedUser } from '../../../../../utils';
import { useAtom, useSetAtom } from 'jotai';
import {
	anyConsentRequiredAtom,
	behaviorAtom,
	campaignAtom,
	campaignIdAtom,
	createInfluencerAtom,
	dataLayerAtom,
	optedInAtom,
	persistUserAtom,
	trackConversionAtom,
} from '../../../../../shared/atoms';

/**
 * @featureflags
 */
const ENABLE_EXPERIMENTAL_SMS_CONSENT = flags.isActive(
	flags.names.ENABLE_EXPERIMENTAL_SMS_CONSENT
);

const ENABLE_EXPERIMENTAL_INCENTIVE_FLOW = flags.isActive(
	flags.names.ENABLE_EXPERIMENTAL_INCENTIVE_FLOW
);

export const OfferOptIn = () => {
	const history = useHistory();
	const intl = useIntl();
	const posthog = usePostHog();
	const [campaignId] = useAtom(campaignIdAtom);
	const [{ campaign }] = useAtom(campaignAtom);
	const [consentRequired] = useAtom(anyConsentRequiredAtom);
	const [dataLayer] = useAtom(dataLayerAtom);
	const [behavior] = useAtom(behaviorAtom);
	const [persistedUser, setPersistedUser] = useAtom(persistUserAtom);
	const [influencer, createInfluencer] = useAtom(createInfluencerAtom);
	const [, trackConversion] = useAtom(trackConversionAtom);
	const setOptedIn = useSetAtom(optedInAtom);

	const { orderId, name } = dataLayer || {};

	const email = dataLayer.email || persistedUser.email;
	const subtotal = dataLayer.subtotal || dataLayer.revenue;

	const requireConsent = {
		email: campaign?.useEmailConsent && !getPersistedUser().emailConsent,
		sms: ENABLE_EXPERIMENTAL_SMS_CONSENT ? campaign?.useSMSConsent : false, // do not require if not enabled
	};

	const onSubmit = async function({ email, emailConsent }) {
		setPersistedUser({
			email,
			emailConsent,
		});

		const SHOW_INCENTIVE =
			ENABLE_EXPERIMENTAL_INCENTIVE_FLOW && behavior?.type === 'incentive';
		const finalDestination = SHOW_INCENTIVE ? '/offer-code' : '/share';
		const conversionType = SHOW_INCENTIVE ? 'claimDiscount' : 'participation';

		const user = await createInfluencer([
			{
				identifier: email,
				name: name || email,
				revenue: subtotal,
				inviteId: dataLayer?.invite,
				transactionId: dataLayer?.transactionId,
				campaignId,
				orderId,
				email,
				emailConsent,
			},
		]);
		await trackConversion([
			{
				name: name || email || 'guest',
				identifier: email || 'guest',
				inviteId: dataLayer?.invite,
				transactionId: dataLayer?.transactionId,
				conversionType,
				email,
				campaignId,
			},
		]);

		setOptedIn(true);

		posthog?.identify(influencer?.id, {
			...influencer,
			flavor: behavior?.flavor,
		});
		posthog?.capture('offer_opt_in');

		history.push(finalDestination);
	};

	/**
	 * localization strings
	 */
	const UNLOCK_INPUT = intl.formatMessage({
		id: 'unlock.input',
		defaultMessage: 'Your Email Address',
		description: 'Label for an input to enter an email',
	});

	const UNLOCK_LABEL = intl.formatMessage({
		id: 'unlock.label',
		defaultMessage: 'Activate Offer',
		description: 'Call to action to unlock offer',
	});

	// skip the email entry step
	useEffect(() => {
		// don't try anything
		if (consentRequired) return;

		if (campaign && email) onSubmit({ email, emailConsent: true });
	}, [campaign]);

	return (
		<PageWrapper>
			<Offer />
			<Typography.P typoSize='sm'>
				<FormattedMessage
					id='optInMessage'
					defaultMessage={
						'Let us know where to contact you with offer and reward updates. Unsubscribe anytime.'
					}
					description={
						'Prompts the user to tell us how to contact them for offer and reward updates'
					}
				/>
			</Typography.P>
			<Div paddingLeft='md' paddingRight='md'>
				{!influencer.isLoading ? (
					<CodeUnlocker
						onSubmit={onSubmit}
						defaultEmail={email}
						termsURL={campaign?.termsAndConditionsURL}
						privacyURL={campaign?.privacyPolicyURL}
						requireConsent={requireConsent}
						label={UNLOCK_INPUT}
						unlockLabel={UNLOCK_LABEL}
					/>
				) : (
					<Div
						padding='xlg'
						textAlign='center'
						backgroundColor='ambientLight'
						borderRadius='default'
						marginBottom='xlg'
					>
						<PopsicleLoader />
						<Typography.P marginTop='none'>
							Unlocking your offer...
						</Typography.P>
					</Div>
				)}
			</Div>
		</PageWrapper>
	);
};
